import { Button, useAlert } from '@ads/components-react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/atoms';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

import { useTutorStudentLimit } from '../../../hooks/useTutorStudentLimit';
import { useTutorStudentUsage } from '../../../hooks/useTutorStudentUsage';
import { formatDate } from '../../../utils/format';
import { USAGE_DEFAULT_RANGE_DAYS } from '../../../utils/tutorAiLimits';
import { MonthlyHistoryList } from '../../organisms/MonthlyHistoryList';
import { StudentLimitKpiRow } from '../../organisms/StudentLimitKpiRow';
import { TokenCompositionCard } from '../../organisms/TokenCompositionCard';
import { TokenLimitForm } from '../../organisms/TokenLimitForm';
import { TokenTrendChart } from '../../organisms/TokenTrendChart';

export interface TutorStudentUsageTemplateProps {
  readonly personId: number;
}

/**
 * Template do relatório de uso e limites de um aluno.
 *
 * A janela é fixa nos últimos {@link USAGE_DEFAULT_RANGE_DAYS} dias: a série diária e o
 * breakdown descrevem consumo recente, e os limites que o operador vai ajustar são sempre
 * do dia, da semana e do mês correntes — que a API devolve independente do período pedido.
 */
export function TutorStudentUsageTemplate({ personId }: TutorStudentUsageTemplateProps) {
  const { t } = useTranslation('tutorAI');
  const { alert } = useAlert();

  const period = useMemo(() => {
    const today = dayjs();
    return {
      start: today.subtract(USAGE_DEFAULT_RANGE_DAYS - 1, 'day').format('YYYY-MM-DD'),
      end: today.format('YYYY-MM-DD'),
    };
  }, []);

  const { usage, isLoading, isError, refetch } = useTutorStudentUsage(personId, period);
  const { save, isSaving } = useTutorStudentLimit(personId, {
    onSaved: () => alert({ alertType: 'success', title: t('student.limitForm.saved') }),
    onError: () => alert({ alertType: 'danger', title: t('student.limitForm.saveError') }),
  });

  return (
    <div className="flex flex-col gap-xs">
      <PageHeader
        title={t('student.usage.title')}
        subtitle={t('student.usage.subtitle', {
          start: formatDate(period.start),
          end: formatDate(period.end),
        })}
      />

      {isError ? (
        <Alert type="danger">
          <div className="flex w-full flex-wrap items-center justify-between gap-mini">
            <span>{t('student.usage.loadError')}</span>
            <Button hierarchy="secondary" size="sm" onClick={() => void refetch()}>
              {t('student.usage.retry')}
            </Button>
          </div>
        </Alert>
      ) : isLoading ? (
        <Alert type="information" description={t('student.usage.loading')} />
      ) : (
        <>
          <StudentLimitKpiRow usage={usage} />
          <TokenTrendChart series={usage.dailyUsage} />
          <TokenCompositionCard breakdown={usage.breakdown} />
          <MonthlyHistoryList history={usage.history} />
          <TokenLimitForm
            defaultValues={{
              dailyLimit: usage.dailyLimit,
              weeklyLimit: usage.weeklyLimit,
              monthlyLimit: usage.monthlyLimit,
              enabled: usage.limitEnabled,
            }}
            onSave={save}
            isSaving={isSaving}
          />
        </>
      )}
    </div>
  );
}
